import React, { useEffect, useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import useBundleTranslation from 'i18n';
import {
    FormControlProps,
    FormComponentValue,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import { IKgModelCard } from 'app/extreport/concierge/answering/kgModelCardTypes';
import { useCheckUserAccess } from 'hooks/useCheckUserAccess';
import { deriveBanner, previewAvailable } from './bannerMessage';
import { useConciergeKgCards } from './useConciergeKgCards';

export interface ConciergeAnsweringBannerProps extends FormControlProps {
    models: IKgModelCard[];
    entityId: number;
    actionUrl: string;
    ownRoute: boolean;
    legacyOwnActive?: boolean;
    biTool: 'powerbi' | 'tableau' | 'other';
    resourceOptions: FormComponentValue[];
    translationNS?: string;
}

const NS = 'components/common/concierge-answering';

export default function ConciergeAnsweringBanner({
    controlProps,
}: FormElementControlPropsType<ConciergeAnsweringBannerProps>) {
    const { t } = useBundleTranslation([controlProps.translationNS ?? NS]);
    const { adminOrPrivilege } = useCheckUserAccess();
    const { cards } = useConciergeKgCards({
        entityId: controlProps.entityId,
        actionUrl: controlProps.actionUrl,
        initial: controlProps.models ?? [],
    });

    const ownRoute = !!controlProps.ownRoute;
    const { hookFormWatch, hookFormGetValues } = controlProps.form;
    const [watched, setWatched] = useState<{ source?: 'own' | 'other'; pickKey?: string }>(() => ({
        source: hookFormGetValues('concierge_answer_source') || undefined,
        pickKey: hookFormGetValues('concierge_primary_resource') || undefined,
    }));

    useEffect(() => {
        const subscription = hookFormWatch((value: Record<string, any>, { name }: { name?: string }) => {
            if (name === 'concierge_answer_source') setWatched((w) => ({ ...w, source: value[name] }));
            if (name === 'concierge_primary_resource') setWatched((w) => ({ ...w, pickKey: value[name] }));
        });
        return () => subscription?.unsubscribe();
    }, [hookFormWatch]);

    const source: 'own' | 'other' = watched.source ?? (ownRoute ? 'own' : 'other');
    const pickKey = watched.pickKey ?? '';
    const option = pickKey ? (controlProps.resourceOptions ?? []).find((o) => o.value === pickKey) : undefined;
    const pick =
        option && !option.props?.deleted
            ? { label: String(option.label), typeLabel: String(option.props?.type_label ?? '') }
            : null;

    const msg = deriveBanner({
        ownRoute,
        biTool: controlProps.biTool,
        source,
        cards,
        pick,
        legacyOwnActive: !!controlProps.legacyOwnActive,
    });
    const { termKey, ...vars } = msg.vars;
    const term = termKey ? t(String(termKey), { count: Number(vars.count) }) : undefined;
    const preview = previewAvailable(msg, cards, adminOrPrivilege('PRIV_MANAGE_KNOWLEDGE_GRAPH'));

    const color = msg.active ? 'success' : 'error';

    return (
        <Box
            data-test="concierge_answering_banner"
            data-state={msg.active ? 'active' : 'not_active'}
            sx={(theme) => ({
                bgcolor: alpha(theme.palette[color].main, 0.05),
                border: 1,
                borderColor: alpha(theme.palette[color].main, 0.35),
                borderRadius: 0.5,
                px: 1.75,
                py: 1.375,
                mb: 0.75,
            })}
        >
            <Typography
                variant="body2"
                data-test="concierge_answering_banner_title"
                sx={{ fontWeight: 600, color: `${color}.main`, mb: 0.5 }}
            >
                {t(msg.active ? 'banner_active' : preview ? 'banner_not_active_preview' : 'banner_not_active')}
            </Typography>
            <Typography variant="body2" color="text.primary">
                {t(msg.key, { ...vars, term })}
            </Typography>
            {preview && (
                <Typography
                    variant="body2"
                    color="text.primary"
                    data-test="concierge_answering_banner_preview"
                    sx={{ mt: 0.5 }}
                >
                    {t('banner_preview_line')}
                </Typography>
            )}
        </Box>
    );
}

export class ConciergeAnsweringBannerBuilder extends FormComponentBuilder {
    prepareProps(): ConciergeAnsweringBannerProps {
        const p = this.elementProps.componentProps;
        return {
            ...this.controlProps,
            models: p?.models ?? [],
            entityId: p?.entityId,
            actionUrl: p?.actionUrl,
            ownRoute: p?.ownRoute,
            legacyOwnActive: p?.legacyOwnActive,
            biTool: p?.biTool,
            resourceOptions: p?.resourceOptions ?? [],
            translationNS: p?.translationNS,
        };
    }
}
